import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { SalesArea } from '@next/core-lib/business-partner-aggregation-api';
import { OrderingInformation } from '@next/core-lib/cart';
import { ElasticApiHit, ElasticApiResponse } from '@next/core-lib/elastic-base';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Environment } from 'src/environments/environment.model';

import { CatalogItem, SkuCatalogItem } from '../models/catalog-item.model';
import { ElasticCatalogItem, ElasticSkuCatalogItem } from '../models/elastic-catalog.models';
import { SortMode } from '../models/sort-mode.model';

export interface ElasticCatalogResult {
  catalogItems: CatalogItem[];
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class ElasticApiService {
  readonly externalIdBoost = 1;
  readonly defaultRequestSize = 50;

  protected baseUrl = `${this.env.API_BASE_URL}/service/elastic/secure`;

  constructor(
    protected http: HttpClient,
    @Inject(EnvironmentInjector) private readonly env: Environment,
  ) {}

  public getCatalogItem(salesAreas: SalesArea[], locale: string, entityId: string): Observable<CatalogItem> {
    const adjustedLocale = this.getAdjustedLocale(salesAreas, locale);

    const query = {
      size: this.defaultRequestSize,
      query: {
        bool: {
          must: [
            {
              bool: {
                should: salesAreas.map((salesArea) => ({
                  match_phrase_prefix: {
                    SalesOrganization: salesArea.salesOrganization,
                  },
                })),
              },
            },
            ...(adjustedLocale
              ? [
                  {
                    match_phrase_prefix: {
                      Locale: adjustedLocale,
                    },
                  },
                ]
              : []),
          ],
          filter: [] as Array<{ match_phrase: { [filterKey: string]: string } }>,
        },
      },
    };

    if (entityId !== undefined) {
      query.query.bool.filter.push({
        match_phrase: {
          EntryId: entityId,
        },
      });
    }

    return this.postToElastic(JSON.stringify(query)).pipe(map((catalogItems) => catalogItems.catalogItems[0]));
  }

  public getCatalogProducts(
    salesAreas: SalesArea[],
    locale: string,
    pathPrefix: string,
    from: number,
    size: number,
    keywords: string,
    sortMode: SortMode,
  ): Observable<ElasticCatalogResult> {
    const adjustedLocale = this.getAdjustedLocale(salesAreas, locale);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query: any = {
      size,
      from,
      sort: this.getQueryStringFromSortMode(sortMode),
      query: {
        bool: {
          must: [
            {
              exists: {
                field: 'Sku.OrderInfo.AllowedOrderTypes',
              },
            },
            {
              bool: {
                should: salesAreas.map((salesArea) => ({
                  match_phrase_prefix: {
                    SalesOrganization: salesArea.salesOrganization,
                  },
                })),
              },
            },
            ...(adjustedLocale
              ? [
                  {
                    match_phrase_prefix: {
                      Locale: adjustedLocale,
                    },
                  },
                ]
              : []),
          ],
          filter: [
            {
              match_phrase: {
                IsMaterial: true,
              },
            },
            {
              match_phrase: {
                'Sku.OrderInfo.Usable': true,
              },
            },
          ],
        },
      },
    };

    if (pathPrefix && pathPrefix !== '/') {
      query.query.bool.must.push({
        bool: {
          should: [
            {
              match_phrase_prefix: {
                Path: pathPrefix,
              },
            },
          ],
        },
      });
    }

    if (keywords?.length > 0) {
      query.query.bool.must.push({
        bool: {
          // @ts-ignore
          should: [
            {
              query_string: {
                query: `*${keywords}*`,
                fields: ['Description', 'EntryId^1'],
                analyze_wildcard: true,
              },
            },
          ],
        },
      });
    }

    return this.postToElastic(JSON.stringify(query)).pipe(map((catalogItems) => catalogItems));
  }

  public getCatalog(salesAreas: SalesArea[], locale: string, isRoot = false, parentId?: string, keywords?: string): Observable<ElasticCatalogResult> {
    const adjustedLocale = this.getAdjustedLocale(salesAreas, locale);

    const query = {
      size: this.defaultRequestSize,
      query: {
        bool: {
          must: [
            {
              bool: {
                should: salesAreas.map((salesArea) => ({
                  match_phrase_prefix: {
                    SalesOrganization: salesArea.salesOrganization,
                  },
                })),
              },
            },
            ...(adjustedLocale
              ? [
                  {
                    match_phrase_prefix: {
                      Locale: adjustedLocale,
                    },
                  },
                ]
              : []),
            {
              term: {
                IsRoot: isRoot,
              },
            },
          ],
          filter: [] as Array<{ match_phrase: { [filterKey: string]: string | boolean } }>,
        },
      },
    };

    if (parentId) {
      query.query.bool.filter = [
        {
          match_phrase: {
            ParentId: parentId,
          },
        },
      ];
    }

    if (keywords?.length > 0) {
      query.query.bool.must.push({
        bool: {
          should: [
            {
              // @ts-ignore
              match_phrase: {
                Title: keywords,
              },
            },
            {
              match_phrase_prefix: {
                // @ts-ignore
                Title: keywords,
              },
            },
            {
              // @ts-ignore
              simple_query_string: {
                query: `*${keywords}*`,
                fields: ['Title', `ExternalSkuId^${this.externalIdBoost}`],
                analyze_wildcard: true,
              },
            },
          ],
        },
      });
    }

    return this.postToElastic(JSON.stringify(query));
  }

  public getSkuCatalogCount(keywords: string, language: string, salesOrganization: string): Observable<number> {
    const query = {
      query: {
        bool: {
          must: [
            {
              exists: {
                field: 'OrderInfo.AllowedOrderTypes',
              },
            },
            {
              bool: {
                should: {
                  match_phrase: {
                    ExternalSalesAreaId: salesOrganization,
                  },
                },
                minimum_should_match: 1,
              },
            },
            {
              bool: {
                should: {
                  match_phrase: {
                    LanguageCode: language,
                  },
                },
                minimum_should_match: 1,
              },
            },
          ],
          filter: [
            {
              match_phrase: {
                'OrderInfo.Usable': true,
              },
            },
          ],
        },
      },
    };

    if (keywords?.length > 0) {
      query.query.bool.must.push({
        bool: {
          // @ts-ignore
          should: [
            {
              query_string: {
                query: `*${keywords}*`,
                fields: ['Title', 'ExternalSkuId^1'],
                analyze_wildcard: true,
              },
            },
          ],
        },
      });
    }
    return this.http.post<{ count: number }>(`${this.baseUrl}/skusearch/count`, query).pipe(map((response) => response.count));
  }

  public getSkuCatalog({ keywords, language, salesOrganization, size = 100, from = 0 }: ElasticApiSkuCatalogRequest): Observable<SkuCatalogItem[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query: any = {
      size,
      from,
      sort: !keywords?.length
        ? [
            {
              'Title.keyword': {
                order: 'asc',
              },
            },
          ]
        : [],
      query: {
        bool: {
          must: [
            {
              exists: {
                field: 'OrderInfo.AllowedOrderTypes',
              },
            },
            {
              bool: {
                should: {
                  match_phrase: {
                    ExternalSalesAreaId: salesOrganization,
                  },
                },
                minimum_should_match: 1,
              },
            },
            {
              bool: {
                should: {
                  match_phrase: {
                    LanguageCode: language,
                  },
                },
                minimum_should_match: 1,
              },
            },
          ],
          filter: [
            {
              match_phrase: {
                'OrderInfo.Usable': true,
              },
            },
          ],
        },
      },
    };

    if (keywords?.length > 0) {
      query.query.bool.must.push({
        bool: {
          // @ts-ignore
          should: [
            {
              query_string: {
                query: `*${keywords}*`,
                fields: ['Title^3', 'ExternalSkuId'],
                analyze_wildcard: true,
              },
            },
            {
              query_string: {
                query: `${keywords}*`,
                fields: ['Title', 'ExternalSkuId^2'],
                analyze_wildcard: true,
              },
            },
            {
              match: {
                Title: `${keywords}`,
              },
            },
            {
              match: {
                ExternalSkuId: `${keywords}`,
              },
            },
          ],
        },
      });
    }

    return this.http.post<ElasticApiResponse<ElasticSkuCatalogItem>>(`${this.baseUrl}/skusearch`, query).pipe(
      map(({ hits }) => this.groupByProductIdAndSortHitsByMaxGroupScore(hits.hits)),
      map((skuCatalogItems) => skuCatalogItems.map((elasticSkuCatalogItem) => this.mapToSkuCatalogItem(elasticSkuCatalogItem))),
      catchError((error: Error) => (error instanceof HttpErrorResponse && error.status === 403 ? of([]) : throwError(() => error))),
    );
  }

  public getEmptyReturnableSkuCatalog({
    keywords,
    language,
    salesOrganization,
    size = 100,
    from = 0,
    materialType = 'ZVRP',
  }: ElasticApiSkuCatalogRequest & { materialType?: string }): Observable<SkuCatalogItem[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query: any = {
      size,
      from,
      sort: !keywords?.length
        ? [
            {
              'Title.keyword': {
                order: 'asc',
              },
            },
          ]
        : [],
      query: {
        bool: {
          must: [
            {
              bool: {
                should: {
                  match_phrase: {
                    ExternalSalesAreaId: salesOrganization,
                  },
                },
                minimum_should_match: 1,
              },
            },
            {
              bool: {
                should: {
                  match_phrase: {
                    LanguageCode: language,
                  },
                },
                minimum_should_match: 1,
              },
            },
          ],
          filter: [
            {
              match_phrase: {
                MaterialType: materialType,
              },
            },
          ],
          must_not: [
            // Exclude articles blocked for return (Z7 and Z9 distribution chain states)
            {
              terms: {
                'OrderInfo.DistributionChainState': ['Z7', 'Z9'],
              },
            },
          ],
        },
      },
    };

    // Only include articles that allow ReturnableOrder
    query.query.bool.must.push({
      match_phrase: {
        'OrderInfo.AllowedOrderTypes': 'ReturnableOrder',
      },
    });

    if (keywords?.length > 0) {
      query.query.bool.must.push({
        bool: {
          // @ts-ignore
          should: [
            {
              query_string: {
                query: `*${keywords}*`,
                fields: ['Title^3', 'ExternalSkuId'],
                analyze_wildcard: true,
              },
            },
            {
              query_string: {
                query: `${keywords}*`,
                fields: ['Title', 'ExternalSkuId^2'],
                analyze_wildcard: true,
              },
            },
            {
              match: {
                Title: `${keywords}`,
              },
            },
            {
              match: {
                ExternalSkuId: `${keywords}`,
              },
            },
          ],
        },
      });
    }

    return this.http.post<ElasticApiResponse<ElasticSkuCatalogItem>>(`${this.baseUrl}/skusearch`, query).pipe(
      map(({ hits }) => this.groupByProductIdAndSortHitsByMaxGroupScore(hits.hits)),
      map((skuCatalogItems) => skuCatalogItems.map((elasticSkuCatalogItem) => this.mapToSkuCatalogItem(elasticSkuCatalogItem))),
      catchError((error: Error) => (error instanceof HttpErrorResponse && error.status === 403 ? of([]) : throwError(() => error))),
    );
  }

  public getSkuCatalogItemsByExternalSkuIds(externalSkuIds: string[], language: string, salesAreas: SalesArea[]): Observable<SkuCatalogItem[]> {
    const query = {
      size: externalSkuIds.length,
      query: {
        bool: {
          must: [
            {
              exists: {
                field: 'OrderInfo.AllowedOrderTypes',
              },
            },
            {
              bool: {
                should: salesAreas.map((salesArea) => ({
                  match_phrase_prefix: {
                    ExternalSalesAreaId: salesArea.salesOrganization,
                  },
                })),
              },
            },
            {
              match_phrase_prefix: {
                LanguageCode: language,
              },
            },
          ],
          filter: [
            {
              terms: {
                SkuId: externalSkuIds,
              },
            },
            {
              match_phrase: {
                'OrderInfo.Usable': true,
              },
            },
          ],
        },
      },
    };

    return this.http.post<ElasticApiResponse<ElasticSkuCatalogItem>>(`${this.baseUrl}/skusearch`, query).pipe(
      map(({ hits }) => hits.hits.map(({ _source }) => _source)),
      map((skuCatalogItems) => skuCatalogItems.map((elasticSkuCatalogItem) => this.mapToSkuCatalogItem(elasticSkuCatalogItem))),
      catchError((error: Error) => (error instanceof HttpErrorResponse && error.status === 403 ? of([]) : throwError(() => error))),
    );
  }

  private postToElastic(query: string) {
    return this.http
      .post<ElasticApiResponse<ElasticCatalogItem>>(`${this.baseUrl}/catalog`, query, {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
      })
      .pipe(
        map(({ hits }) => {
          const elasticCatalogItems: ElasticCatalogItem[] = hits.hits.map((hit) => hit._source);

          const filteredItems = elasticCatalogItems.filter((item) => {
            if (item.IsMaterial && item.Sku?.OrderInfo) {
              const usable = item.Sku.OrderInfo.Usable;
              const allowedOrderTypes = item.Sku.OrderInfo.AllowedOrderTypes;
              if (usable === false || !allowedOrderTypes || allowedOrderTypes.length === 0) {
                return false;
              }
            }
            return true;
          });

          return {
            catalogItems: filteredItems.map((elasticCatalogItem) => this.mapCatalogItem(elasticCatalogItem)),
            count: filteredItems.length,
          };
        }),
      );
  }

  private getQueryStringFromSortMode(sortMode: SortMode) {
    // Note that the '.keyword' suffix is necessary as we're sorting on a text field, which by default are not available for (a.o.) sorting.
    // This is why the text fields have a multi-field mapping, in which 'keyword' is the unanalyzed keyword field used for sorting.
    switch (sortMode) {
      case SortMode.default:
        return [];

      case SortMode.articleNameAsc:
        return [{ 'Description.keyword': { order: 'asc' } }];

      case SortMode.articleNameDesc:
        return [{ 'Description.keyword': { order: 'desc' } }];

      case SortMode.articleNumberAsc:
        return [{ 'EntryId.keyword': { order: 'asc' } }];

      case SortMode.articleNumberDesc:
        return [{ 'EntryId.keyword': { order: 'desc' } }];
    }
  }

  private mapCatalogItem({
    Description,
    EntryId,
    ParentId,
    Path,
    SalesOrganization,
    Locale,
    SortNumber,
    IsMaterial,
    IsRoot,
    Sku,
  }: ElasticCatalogItem): CatalogItem {
    return {
      description: Description,
      entryId: EntryId,
      path: Path,
      parentId: ParentId,
      salesOrganization: SalesOrganization,
      locale: Locale,
      sortNumber: SortNumber,
      isMaterial: IsMaterial,
      isRoot: IsRoot,
      sku: Sku ? this.mapToSkuCatalogItem(Sku) : null,
    };
  }

  private groupByProductIdAndSortHitsByMaxGroupScore(hits: ElasticApiHit<ElasticSkuCatalogItem>[]): ElasticSkuCatalogItem[] {
    const groupedHits = this.groupHitsByProductId(hits);

    // Convert the grouped hits to an array of entries and sort by max _score
    const groupsSortedByMaxScore = Object.entries(groupedHits)
      .map(([productId, hitsInGroup]) => {
        const sortedGroup = hitsInGroup.sort((a, b) => b._score - a._score);
        const maxScoreInGroup = Math.max(...hitsInGroup.map((hit) => hit._score));
        return { productId, sortedGroup, maxScoreInGroup };
      })
      .sort((a, b) => b.maxScoreInGroup - a.maxScoreInGroup);

    // Flatten the sorted grouped hits back to the original structure
    return groupsSortedByMaxScore.flatMap((group) => group.sortedGroup).map((hit) => hit._source);
  }

  private groupHitsByProductId(hits: ElasticApiHit<ElasticSkuCatalogItem>[]): Record<string, ElasticApiHit<ElasticSkuCatalogItem>[]> {
    return hits.reduce(
      (acc, hit) => {
        const productId = hit._source.ProductId;
        if (!acc[productId]) {
          acc[productId] = [];
        }
        acc[productId].push(hit);
        return acc;
      },
      {} as Record<string, ElasticApiHit<ElasticSkuCatalogItem>[]>,
    );
  }

  private mapToSkuCatalogItem(sku: ElasticSkuCatalogItem): SkuCatalogItem {
    const orderingInformation = {
      orderUnitQuantity: sku.OrderInfo.OrderUnitQuantity,
      minimumOrderUnitQuantity: sku.OrderInfo.MinimumOrderUnitQuantity,
      siloDistributionRequirements: sku.OrderInfo.SiloDistributionRequirements,
      orderUnit: sku.OrderInfo.OrderUnit,
      flexibility: sku.OrderInfo.Flexibility,
      palletOrderUnitQuantity: sku.OrderInfo.PalletOrderUnitQuantity,
      maximumOrderUnitQuantity: sku.OrderInfo.MaximumOrderUnitQuantity,
      increaseOrderUnitQuantity: sku.OrderInfo.IncreaseOrderUnitQuantity,
      transportGroup: sku.OrderInfo.TransportGroup,
      usable: sku.OrderInfo.Usable,
      allowedUsers: sku.OrderInfo.AllowedUsers,
      allowedOrderTypes: sku.OrderInfo.AllowedOrderTypes,
      closingTimeStandard: sku.OrderInfo.ClosingTimeStandard,
      closingTimeRush: sku.OrderInfo.ClosingTimeRush,
      closingTimeEmergency: sku.OrderInfo.ClosingTimeEmergency,
      distributionChainState: sku.OrderInfo.DistributionChainState,
      useFixedFlexibility: sku.OrderInfo.UseFixedFlexibility,
    } as OrderingInformation;
    return {
      externalSkuId: sku.ExternalSkuId,
      skuId: sku.SkuId,
      productId: sku.ProductId,
      title: sku.Title,
      hasProductSupplements: sku.HasProductSupplements,
      orderingInformation,
      packagingInformation: {
        packageDescription: sku.PackageInfo.PackageDescription,
      },
    };
  }

  private getAdjustedLocale(salesAreas: SalesArea[], locale: string): string | null {
    const isBelgianSalesOrg = salesAreas[0]?.salesOrganization.startsWith('BE');
    if (isBelgianSalesOrg && (locale === 'nl' || locale === 'fr')) {
      return locale;
    } else if (isBelgianSalesOrg) {
      return 'nl';
    }
    return null;
  }
}

export interface ElasticApiSkuCatalogRequest {
  keywords: string;
  language: string;
  size?: number;
  from?: number;
  salesOrganization: string;
}
